import React from "react";
import "react-datepicker/dist/react-datepicker.css";
import { SlArrowDown, SlCalender } from "react-icons/sl";
import DatePicker from "react-datepicker";
import dayjs from "dayjs";
import { formatDateToYYYYMMDD } from "utils/dateFormatters";
import { DatePickerPopperContainer } from "./DatePickerPopperContainer";

interface DateRangeType {
  startDate: Date;
  endDate: Date;
}

interface DateInputProps {
  disabled?: boolean;
  name: string;
  value: string | null;
  handleChange: (e: string | null) => void;
  normal?: boolean;
  minDate?: Date;
  placeholder?: string;
  disabledDates?: Date[] | DateRangeType[] | null;
  /** Calendar dates (YYYY-MM-DD) already taken; avoids local Date timezone drift in filterDate. */
  disabledDateStrings?: string[] | null;
  disabledTextRight?: boolean;
  showChevron?: boolean;
  size?: string;
  background?: string;
  format?: string;
}

export const DateInput: React.FC<DateInputProps> = ({
  disabled = false,
  name: _name,
  value,
  handleChange,
  normal = true,
  minDate,
  placeholder = "Select Date",
  disabledDates,
  disabledDateStrings,
  disabledTextRight = true,
  showChevron = true,
  size = "h-[50px] py-[26px]",
  background = "",
  format = "MMMM d, yyyy",
}) => {
  const baseStyle =
    "w-full bg-muted text-foreground transition duration-200 ease-linear appearance-none focus:outline-none text-regular-2 px-4 placeholder:text-muted-foreground/70 disabled:cursor-not-allowed disabled:opacity-100";
  const withRoundBorder = "rounded-xl border border-input focus:border-primary";

  const selectedDate = value ? dayjs(value.slice(0, 10), "YYYY-MM-DD", true).toDate() : null;

  const disabledDateStringSet = new Set(
    (disabledDateStrings ?? []).map((date) => date.slice(0, 10))
  );

  // Convert disabledDates to Date[] format for the filterDate function
  const getDisabledDates = (): Date[] => {
    if (!disabledDates) return [];

    return disabledDates.map((item) => {
      if (item instanceof Date) {
        return item;
      } else {
        // Handle DateRangeType format
        return item.startDate;
      }
    });
  };

  const showTrailingIcons = !disabled;

  return (
    <div className="relative w-full">
      <DatePicker
        selected={selectedDate}
        onChange={(date: Date | null) => {
          if (date) {
            handleChange(formatDateToYYYYMMDD(date));
          } else {
            handleChange(null);
          }
        }}
        placeholderText={placeholder}
        minDate={minDate}
        disabled={disabled}
        dateFormat={format}
        className={`${baseStyle} ${size} ${background} placeholder:text-xs ${
          showTrailingIcons ? (showChevron ? "pr-12" : "pr-10") : ""
        } ${
          disabled && disabledTextRight ? "text-right" : "text-left"
        } ${disabled ? "cursor-default" : ""} ${normal ? "" : withRoundBorder}`}
        wrapperClassName="w-full"
        popperContainer={DatePickerPopperContainer}
        popperProps={{ strategy: "fixed" }}
        popperPlacement="bottom-start"
        showPopperArrow={false}
        filterDate={(date) => {
          const calendarDay = formatDateToYYYYMMDD(date);
          if (disabledDateStringSet.has(calendarDay)) {
            return false;
          }
          const disabledDatesArray = getDisabledDates();
          if (disabledDatesArray.length === 0) return true;
          return !disabledDatesArray.some((d) => dayjs(d).isSame(date, "day"));
        }}
      />
      {showTrailingIcons ? (
        <div className="absolute right-0 top-0 flex h-full items-center pr-3 pointer-events-none">
          <div className="flex row gap-x-2">
            <SlCalender className="text-muted-foreground" />
            {showChevron ? <SlArrowDown className="my-auto text-muted-foreground" /> : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};
